import type { MessageAction, RequestAction } from "trystero";
import type { OnlineRoomRecovery } from "./online-room-recovery.js";
import { requestRoomPacket } from "./online-room-request.js";
import type {
  ControlReply,
  ControlRequest,
  LobbyPacket,
  OnlineRoomSnapshot,
  OnlineRosterEntry,
  RecoveryDescriptor,
} from "./online-room-types.js";
import {
  decodeBoundedRoomPacket,
  encodeRoomPacket,
  type OnlineRoomAuthority,
  type RoomOrder,
  type RoomOrderAck,
  type RoomStatePacket,
} from "./room-protocol.js";
import type { ConflictConfig } from "./types.js";

interface ActionBindings {
  readonly lobby: MessageAction<string>;
  readonly control: RequestAction<string, string>;
  readonly order: RequestAction<string, string>;
  readonly state: MessageAction<string>;
  readonly sessionId: string;
  readonly hostPeerId: () => string | undefined;
  readonly authority: () => OnlineRoomAuthority | undefined;
  readonly recovery: OnlineRoomRecovery;
  readonly handleControl: (raw: string, peerId: string) => Promise<ControlReply>;
  readonly update: (change: Partial<OnlineRoomSnapshot>) => void;
}

export async function handleOnlineControl(
  raw: string,
  peerId: string,
  authority: OnlineRoomAuthority | undefined,
  start: (peerId: string) => void,
  publishLobby: () => Promise<void>,
): Promise<ControlReply> {
  try {
    if (!authority) throw new TypeError("Only the room host accepts controls");
    const request = decodeBoundedRoomPacket<ControlRequest>(raw);
    if (request.kind === "sync") await publishLobby();
    else if (request.kind === "ready") {
      authority.setReady(peerId, request.ready === true);
      await publishLobby();
    } else if (request.kind === "start") start(peerId);
    else if (request.kind === "request-pause")
      authority.requestPause(peerId, request.paused === true);
    else if (request.kind === "approve-pause") authority.approvePause(peerId);
    else throw new TypeError("Unknown room control request");
    return { ok: true };
  } catch (error) {
    return { ok: false, reason: onlineErrorMessage(error) };
  }
}

export function createOnlineOrder(
  snapshot: OnlineRoomSnapshot,
  authorityEpoch: number,
  controllerSequence: number,
  order: Pick<RoomOrder, "requestId" | "expectedScopeGeneration" | "intent">,
): RoomOrder {
  return {
    ...order,
    roomId: snapshot.roomId,
    authorityEpoch,
    controllerGeneration: snapshot.state?.controllerGeneration ?? 0,
    controllerSequence,
  };
}

export function onlineErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : "Online room failed";
}

export async function requestOnlineControl(
  action: RequestAction<string, string>,
  request: ControlRequest,
  hostPeerId: string,
): Promise<void> {
  const reply = await requestRoomPacket<ControlReply>(action, request, hostPeerId);
  if (!reply.ok) throw new TypeError(reply.reason ?? "Room request rejected");
}

export function onlinePeerCounts(
  peers: Readonly<Record<string, RTCPeerConnection>>,
): Pick<OnlineRoomSnapshot, "peerCount" | "directConnections"> {
  const values = Object.values(peers);
  return {
    peerCount: values.length,
    directConnections: values.filter((peer) => peer.connectionState === "connected").length,
  };
}

export async function publishOnlineLobby(
  action: MessageAction<string>,
  authority: OnlineRoomAuthority,
  config: ConflictConfig,
  recovery: RecoveryDescriptor | undefined,
  target?: string,
): Promise<readonly OnlineRosterEntry[]> {
  const roster = authority.roster();
  await action.send(
    encodeRoomPacket({
      config,
      roster,
      started: authority.started,
      ...(recovery ? { recovery } : {}),
    } satisfies LobbyPacket),
    target ? { target } : undefined,
  );
  return roster;
}

export async function publishOnlineState(
  action: MessageAction<string>,
  authority: OnlineRoomAuthority,
  selfId: string,
  peerIds: readonly string[],
  target?: string,
): Promise<RoomStatePacket> {
  for (const peerId of target ? [target] : peerIds)
    await action.send(encodeRoomPacket(authority.stateFor(peerId)), { target: peerId });
  return authority.stateFor(selfId);
}

export function bindOnlineRoomActions(bindings: ActionBindings): void {
  bindings.lobby.onMessage = (raw, context) => {
    if (context.peerId !== bindings.hostPeerId()) return;
    const packet = decodeBoundedRoomPacket<LobbyPacket>(raw);
    const own = packet.roster.find((entry) => entry.sessionId === bindings.sessionId);
    if (packet.recovery) bindings.recovery.initialize(packet.recovery);
    bindings.update({
      phase: packet.started ? "playing" : "lobby",
      config: packet.config,
      roster: packet.roster,
      seatId: own?.seatId ?? null,
    });
  };
  bindings.control.onRequest = async (raw, context) => {
    if (!bindings.authority()) throw new TypeError("Only the room host accepts controls");
    return encodeRoomPacket(await bindings.handleControl(raw, context.peerId));
  };
  bindings.order.onRequest = (raw, context) => {
    const authority = bindings.authority();
    if (!authority) throw new TypeError("Only the room host accepts orders");
    const reply: RoomOrderAck = authority.submit(context.peerId, decodeBoundedRoomPacket(raw));
    return encodeRoomPacket(reply);
  };
  bindings.state.onMessage = (raw, context) => {
    if (context.peerId !== bindings.hostPeerId()) return;
    const state = decodeBoundedRoomPacket<RoomStatePacket>(raw);
    if (!bindings.recovery.observeState(state.authorityEpoch, state.observation.gameTimeMs)) return;
    bindings.update({ phase: "playing", state });
  };
}
